import { useEffect, useState } from "react";
import "./AddSchool.css";
import TopImage from "../../common/TopImage";
import { Images } from "../../../../config/images";
import { DashboardStats } from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../common/CustomInput";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useCreateSubSchool } from "../../../../hooks/organization/school/useCreateSubSchool";
import { useAssignLicense } from "../../../../hooks/organization/useAssignLicense";
import { useGetOrganizationStats } from "../../../../hooks/organization/useGetOrganizationStats";
import SuccessModal from "../../../common/SuccessModal";
import CustomTextArea from "../../../common/CustomTextArea";
import BackButton from "../../../common/BackButton";
import { handleSmartBack } from "../../../../utils/navigationHelper";

const AddSchool = () => {
  const navigate = useNavigate();
  const { createSubSchool, loading, error } = useCreateSubSchool();
  const { assignLicense, loading: licenseLoading } = useAssignLicense();
  const {
    stats: orgStats,
    setStats: setOrgStats,
    loading: statsLoading,
    getOrganizationStats,
  } = useGetOrganizationStats();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successTitle, setSuccessTitle] = useState("");
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch organization stats on mount
  useEffect(() => {
    getOrganizationStats();
  }, []);

  // Stats data from API
  const stats = [
    {
      label: "Total Licenses Purchased",
      value: statsLoading
        ? "..."
        : (orgStats?.licenses?.total ?? 0).toLocaleString(),
      icon: "fa-user",
      colorClass: "dashboard-stat-card-green",
    },
    {
      label: "Licenses Assigned",
      value: statsLoading
        ? "..."
        : (orgStats?.licenses?.assigned ?? 0).toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-peach",
    },
    {
      label: "Licenses Available",
      value: statsLoading
        ? "..."
        : (orgStats?.licenses?.remaining ?? 0).toLocaleString(),
      icon: "fa-file-arrow-down",
      colorClass: "dashboard-stat-card-pastel-blue",
    },
  ];

  const availableLicenses = orgStats?.licenses?.remaining ?? 0;
  const maxAssignLicenses = Math.min(availableLicenses, 10000);

  const validationSchema = Yup.object({
    name: Yup.string()
      .required("School Name is required")
      .min(3, "School Name must be at least 3 characters")
      .max(100, "School Name must be at most 100 characters"),
    subdomain: Yup.string()
      .required("Subdomain is required")
      .min(3, "Subdomain must be at least 3 characters")
      .max(100, "Subdomain must be at most 100 characters")
      .matches(
        /^[a-zA-Z0-9-]+$/,
        "Subdomain can only contain alphabets, numbers, and hyphens",
      ),
    email: Yup.string()
      .email("Invalid email format")
      .required("School Email is required")
      .max(100, "Email must be at most 100 characters"),
    phone: Yup.string()
      .matches(/^[0-9]*$/, "Phone number must contain only digits")
      .length(10, "Phone number must be exactly 10 digits")
      .notRequired(),
    address: Yup.string()
      .max(255, "Address must be at most 255 characters")
      .notRequired(),
    adminName: Yup.string()
      .required("School Admin Name is required")
      .min(3, "Admin Name must be at least 3 characters")
      .max(100, "Admin Name must be at most 100 characters"),
    adminEmail: Yup.string()
      .email("Invalid email format")
      .required("School Admin Email is required")
      .max(100, "Admin Email must be at most 100 characters"),
    assignLicenses: Yup.number()
      .min(0, "License count cannot be negative")
      .max(
        maxAssignLicenses,
        availableLicenses > 10000
          ? "Cannot assign more than 10,000 licenses in a single request"
          : `Cannot add more than ${availableLicenses} licenses (available in organization)`,
      )
      .notRequired(),
    remarks: Yup.string()
      .max(500, "Remarks must be at most 500 characters")
      .notRequired(),
    image: Yup.mixed()
      .notRequired()
      .test(
        "fileSize",
        "Image must be less than 2MB",
        (value) => !value || value.size <= 2 * 1024 * 1024,
      )
      .test(
        "fileType",
        "Only PNG, JPG, and JPEG images are allowed",
        (value) =>
          !value ||
          ["image/png", "image/jpg", "image/jpeg"].includes(value.type),
      ),
  });

  const handleCreateSchool = async (values, { resetForm }) => {
    const schoolData = {
      name: values.name,
      email: values.email,
      subdomain: values.subdomain,
      phone: values.phone || null,
      address: values.address || null,
      image: values.image || null,
      user: {
        f_name: values.adminName,
        email: values.adminEmail,
        role_slug: "school-admin",
      },
    };

    const schoolCreated = await createSubSchool(schoolData, async (id) => {
      // School created successfully, now try to assign licenses if user selected
      if (values.assignLicenses
        && id) {
        const licenseData = {
          sub_school_institution_id: id,
          license_count: parseInt(values.assignLicenses, 10),
          ...(values.remarks && { remark: values.remarks }),
        };

        const licenseAssigned = await assignLicense(licenseData);

        if (licenseAssigned) {
          // Both school creation and license assignment succeeded
          // Update license counts locally to avoid extra API call
          const assignedCount = parseInt(values.assignLicenses, 10);
          setOrgStats((prev) => ({
            ...prev,
            licenses: {
              ...prev?.licenses,
              assigned: (prev?.licenses?.assigned ?? 0) + assignedCount,
              remaining: (prev?.licenses?.remaining ?? 0) - assignedCount,
            },
          }));
          setSuccessTitle("School Created Successfully");
          setSuccessMessage(
            "The school, admin, and licenses have been assigned successfully. An email will be sent to the admin to set their password."
          );
        } else {
          // School created but license assignment failed
          setSuccessTitle("School Created Successfully");
          setSuccessMessage(
            "The school and admin have been created. However, we were unable to assign the licenses. You can assign them while updating the school details."
          );
        }
        resetForm();
        setImagePreview(null);
        setShowSuccessModal(true);
      } else {
        // School created without license assignment
        setSuccessTitle("School Created Successfully");
        setSuccessMessage(
          "The school and admin have been created. An email will be sent to the admin to set their password."
        );
        resetForm();
        setImagePreview(null);
        setShowSuccessModal(true);
      }
    });

    // If school creation failed, the hook will handle the error display
    if (!schoolCreated) {
      return;
    }
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      subdomain: "",
      email: "",
      phone: "",
      address: "",
      adminName: "",
      adminEmail: "",
      password: "",
      assignLicenses: "",
      remarks: "",
      image: null,
    },
    validationSchema,
    onSubmit: handleCreateSchool,
  });

  const handleAddAnotherSchool = () => {
    setShowSuccessModal(false);
    setImagePreview(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue("image", file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveImage = () => {
    formik.setFieldValue("image", null);
    setImagePreview(null);
  };

  const handleGoToSchoolList = () => {
    setShowSuccessModal(false);
    navigate("/organization/school");
  };

  return (
    <div className="add-school-cnt">
      <div className="pb-2">
        <BackButton />
      </div>
      {/* Top Banner */}
      <TopImage text="ADD SCHOOL" imageSrc={Images.TopImage} />

      {/* Stats Cards */}
      <div className="container-fluid p-0">
        {statsLoading && !orgStats ? (
          <DashboardStatsSkeleton count={3} />
        ) : (
          <DashboardStats stats={stats} />
        )}
      </div>

      {/* Form Section */}
      <div className="add-school-form-section">
        <h2 className="school-section-title">ADD NEW SCHOOL</h2>
        <p className="school-section-subtitle">
          Create and manage schools under your organization
        </p>
        <div className="separator" style={{ marginBottom: "30px" }} />

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            {/* School Details */}
            <div className="row mb-4">
              <div className="col-md-6">
                <CustomInput
                  label="School Name"
                  name="name"
                  type="text"
                  placeholder="Enter school name"
                />
              </div>
              <div className="col-md-6">
                <CustomInput
                  label="Subdomain"
                  name="subdomain"
                  type="text"
                  placeholder="Enter subdomain"
                />
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-6">
                <CustomInput
                  label="School Email"
                  name="email"
                  type="email"
                  placeholder="Enter school email"
                />
              </div>
              <div className="col-md-6">
                <CustomInput
                  label="Phone Number"
                  name="phone"
                  type="text"
                  placeholder="Enter phone number"
                  showAstreisk={false}
                />
              </div>
            </div>

            <div className="row mb-5">
              <div className="col-12">
                <CustomTextArea
                  label="Address"
                  name="address"
                  placeholder="Enter school address"
                  showAstreisk={false}
                />
              </div>
            </div>

            {/* School Image Upload */}
            <div className="row mb-5">
              <div className="col-md-6">
                <label className="form-label fw-semibold">School Image <span style={{ color: '#888', fontWeight: 'normal' }}>(Optional)</span></label>
                <div
                  style={{
                    border: '2px dashed #c5c5c5',
                    borderRadius: '10px',
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#fafafa',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  onClick={() => document.getElementById('school-image-input').click()}
                >
                  <input
                    id="school-image-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                  {imagePreview ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={imagePreview}
                        alt="School preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '150px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          background: '#e74c3c',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <i className="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '28px', color: '#888', marginBottom: '8px' }}></i>
                      <p style={{ margin: 0, color: '#888', fontSize: '14px' }}>Click to upload school image</p>
                      <p style={{ margin: 0, color: '#aaa', fontSize: '12px' }}>PNG, JPG, JPEG (Max 2MB)</p>
                    </div>
                  )}
                </div>
                {formik.errors.image && formik.touched.image && (
                  <div className="custom-error" style={{ marginTop: '6px' }}>{formik.errors.image}</div>
                )}
              </div>
            </div>

            {/* School Admin Details */}
            <h3 className="form-subheader">SCHOOL ADMIN DETAILS</h3>
            <div className="separator" style={{ marginBottom: "25px" }} />

            <div className="row mb-4">
              <div className="col-md-6">
                <CustomInput
                  label="Admin Name"
                  name="adminName"
                  type="text"
                  placeholder="Enter admin name"
                />
              </div>
              <div className="col-md-6">
                <CustomInput
                  label="Admin Email"
                  name="adminEmail"
                  type="email"
                  placeholder="Enter admin email"
                />
              </div>
            </div>

            {/* License Allocation */}
            <h3 className="form-subheader">LICENSE ALLOCATION</h3>
            <div className="separator" style={{ marginBottom: "25px" }} />

            <div className="row mb-4">
              <div className="col-md-12">
                <CustomInput
                  label="Assign Licenses"
                  name="assignLicenses"
                  type="number"
                  placeholder="Enter assign licenses"
                  showAstreisk={false}
                  disabled={loading || licenseLoading}
                />
                <div style={{
                  backgroundColor: "#e8f4fd",
                  border: "1px solid #b3d9f2",
                  borderRadius: "6px",
                  padding: "8px 12px",
                  marginTop: "8px",
                  fontSize: "13px",
                  color: "#1a6fa8",
                }}>
                  <i className="fa-solid fa-circle-info me-2"></i>
                  {statsLoading
                    ? "Checking available licenses..."
                    : `Available licenses in organization: ${availableLicenses} | Max per request: ${maxAssignLicenses}`}
                </div>

              </div>
            </div>

            <div className="row mb-5">
              <div className="col-12">
                <CustomTextArea
                  label="Remarks (Optional)"
                  name="remarks"
                  placeholder="Enter remarks"
                  showAstreisk={false}
                />
              </div>
              {error && <div className="custom-error pb-2">{error}</div>}
            </div>

            {/* Action Buttons */}
            <div className="custom-modal-action-container">
              {/* Error Display */}

              <Button
                className="custom-modal-secondary-button"
                onClick={() => handleSmartBack(navigate, "/organization/school")}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="custom-modal-primary-button "
                disabled={loading || licenseLoading}
              >
                {loading || licenseLoading ? "Creating..." : "Create School"}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </div>

      {/* Success Modal */}
      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        title={successTitle}
        message={successMessage}
        primaryButtonText="Add Another"
        secondaryButtonText="School List"
        onPrimaryClick={handleAddAnotherSchool}
        onSecondaryClick={handleGoToSchoolList}
      />
    </div>
  );
};

export default AddSchool;
